import { Link, Flex } from '@servicetitan/anvil2';
import { FinancingApi } from '@servicetitan/financing-api-client';
import { LoadingScreen } from '@servicetitan/financing-utils-shared';
import { useQuery } from '@tanstack/react-query';
import { useDependencies } from '@servicetitan/react-ioc';
import { useState, useEffect, useRef } from 'react';
import { PlansList } from './PlansList';
import { NoPlansAvailableState } from './NoPlansAvailableState';
import { offerToCalculationAdapter } from './offerToCalculationAdapter';

interface OffersContainerProps {
    loanId: string;
}

const REFETCH_INTERVAL = 3000; // 3 seconds
const MAX_RETRY_TIME = 60000; // 60 seconds

export const OffersContainer = ({ loanId }: OffersContainerProps) => {
    const [financingApi] = useDependencies(FinancingApi);
    const [hasTimedOut, setHasTimedOut] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout>();

    // Reset hasTimedOut when loanId changes
    useEffect(() => {
        setHasTimedOut(false);
    }, [loanId]);

    const { data: offers = [], isLoading } = useQuery({
        queryKey: ['offers', loanId],
        queryFn: async () => {
            const response = await financingApi.offersGetOffers(loanId);
            return response.data.offers ?? [];
        },
        enabled: !!loanId && !hasTimedOut,
        refetchInterval: query => {
            // Only refetch if we have no offers and haven't timed out
            // Handle the case when query.state.data is undefined or has length 0
            const hasNoOffers = !query.state.data || query.state.data.length === 0;
            return hasNoOffers && !hasTimedOut ? REFETCH_INTERVAL : false;
        },
        refetchOnWindowFocus: false,
        retry: false,
    });

    // Set up timeout when query starts
    useEffect(() => {
        if (loanId) {
            // Clear any existing timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                setHasTimedOut(true);
            }, MAX_RETRY_TIME);

            return () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
            };
        }
    }, [loanId]); // Removed hasTimedOut from dependencies

    // Clear timeout when we get offers
    useEffect(() => {
        if (offers.length > 0 && timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    }, [offers]);

    // Convert offers to calculations for PlansList
    const calculations = offers.map(offer => offerToCalculationAdapter(offer));

    // Show loading during initial load or while retrying (before timeout)
    const showLoading = !!loanId && (isLoading || (offers.length === 0 && !hasTimedOut));

    // If no loanId, don't show anything
    if (!loanId) {
        return null;
    }

    // Center the content using Flex
    return (
        <Flex justifyContent="center" style={{ width: '100%' }}>
            <div style={{ maxWidth: '845px', width: '100%' }}>
                {showLoading ? (
                    <LoadingScreen message="Loading available offers..." />
                ) : offers.length === 0 ? (
                    <NoPlansAvailableState />
                ) : (
                    <PlansList
                        plans={calculations}
                        instructions={
                            <>
                                Before selecting your offer below, please review the{' '}
                                <Link
                                    href="https://turns.s3.us-east-1.amazonaws.com/Summary+of+Credit+Terms+ST.pdf"
                                    title="Summary of Credit Terms"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Summary of Credit Terms
                                </Link>{' '}
                                which summarizes in great detail key terms of the Credit Agreement
                                that will govern your revolving account.
                            </>
                        }
                    />
                )}
            </div>
        </Flex>
    );
};
